// Catalog.ID Client — Standalone Voucher Purchase
// ============================================================================
// Option (a) of the registration entry choice (registration-restructure §7.3):
// buy registration voucher(s) without registering anything — for yourself later,
// or to give away. Deliberately a route of its own (`/vouchers`) rather than a
// branch inside the wizard: "buy and stop" partway through a wizard would leave
// generated keys and a stash behind for a registration that never happens.
//
// No account is required. The purchaser pays, the payment proof converts to
// codes server-side, and the codes are DISPLAYED here — there is no email
// delivery anywhere in this system, by design. That makes the display the only
// copy, so the page says so loudly and refuses to navigate away by itself.

import { createElement } from '@catalog-shell';
import { createBitsField } from '../components/bits-field';
import { buyVouchers, fetchRegistrationConfig, isTestHookActive } from './payment-flow';
import { registrationOffer } from './registration-gate';
import {
    bitsEuroLabel, mintVoucherBatch,
    type MintedVoucherBatch,
} from './voucher-mint';
import { renderVoucherCodes } from './voucher-code-display';
import {
    normalizeVoucherCode, formatVoucherCode, deriveVoucherSecrets, voucherUrl, VOUCHER_CODE_PLACEHOLDER,
} from './voucher-code';
import {
    mintInvitationCode, formatInvitationCode, deriveInvitationSecrets,
    hashFixedValue, normaliseEmail, normalisePhone,
} from './invitation-document';
import { activationUrlFor } from './delegate-activation-ref';
import { createInvitation, type CreateInvitationRequest } from '../api/invitation';
import { MAX_VOUCHER_QUANTITY } from '../api/payment';
import { lockKeyFor } from '../crypto/bitcash';

import { buildInvitationCardSheet, type InvitationCardData } from '../components/booklet/invitation-card';
import {
    buildVoucherCardSheet, buildVoucherCardDocument, type VoucherCardData,
} from '../components/booklet/voucher-card';
import { printPdf, downloadBlob } from '../components/pdf-loader';
import { getDashboardPort } from '../state/dashboard-context';
import { bitcashWorker } from '../state/bitcash-context';
import { unitsToWholeBit } from '../api/bitcash-units';
import {
    relockBits, relockPlanFor, INVITATION_LOCK_GRACE_MS, type RelockOutcome, type RelockLeg,
} from './bits-release';

export interface VoucherPurchaseCallbacks {
    /** Back to the login screen. */
    onExit: () => void;
    /** Start a personal registration (the "I'll use one now" path). */
    onRegister: () => void;
}

export interface VoucherPurchaseOptions {
    /** A code to prefill the "personalize" section with. The entry page links
     *  here with `#code=RGV-…` when a holder chose *Personalize it for someone
     *  else* (§7), and the fragment is read here rather than in the router so
     *  that the code reaches no access log on the way. */
    personalizeCode?: string;
}

/** Read `#code=` off the current URL. Returns null outside a browser. */
function personalizeCodeFromFragment(): string | null {
    try {
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const code = params.get('code')?.trim();
        return code && normalizeVoucherCode(code) ? code : null;
    } catch {
        return null;
    }
}

export function renderVoucherPurchaseView(
    container: HTMLElement,
    callbacks: VoucherPurchaseCallbacks,
    options: VoucherPurchaseOptions = {},
): void {
    container.innerHTML = '';
    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-auth-card--wide' });

    card.appendChild(createElement('h2', { className: 'cid-auth-title', textContent: 'Catalog.ID' }));
    card.appendChild(createElement('p', {
        className: 'cid-auth-subtitle',
        textContent: 'Buy registration vouchers',
    }));

    const intro = createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'A voucher pays one registration fee and carries bits — spending money for the '
            + 'account it registers, which lands in that account\'s wallet at the end of its '
            + 'registration. Give the code to whoever will register, or keep it for yourself: a code '
            + 'is not tied to any account until it is redeemed.',
    });
    card.appendChild(intro);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    card.appendChild(errorEl);

    const statusEl = createElement('p', { className: 'cid-wizard-status' });

    // ── Form ────────────────────────────────────────────────────────────────

    const typeRow = createElement('div', { className: 'cid-auth-field' });
    typeRow.appendChild(createElement('label', {
        className: 'cid-wizard-text', textContent: 'Registration type',
    }));
    const typeSelect = createElement('select', { className: 'cid-auth-input' });
    for (const [value, label] of [
        ['personal', 'Personal identity'],
        ['organization', 'Organization'],
    ] as const) {
        const opt = createElement('option', { textContent: label });
        opt.value = value;
        typeSelect.appendChild(opt);
    }
    typeRow.appendChild(typeSelect);
    card.appendChild(typeRow);

    const qtyRow = createElement('div', { className: 'cid-auth-field' });
    qtyRow.appendChild(createElement('label', {
        className: 'cid-wizard-text', textContent: 'How many' ,
    }));
    const qtyInput = createElement('input', { className: 'cid-auth-input' });
    qtyInput.type = 'number';
    qtyInput.min = '1';
    // D24: bits ride every voucher, so one checkout mints up to 50 locks inside
    // one webhook. Larger orders are several checkouts.
    qtyInput.max = String(MAX_VOUCHER_QUANTITY);
    qtyInput.value = '1';
    qtyRow.appendChild(qtyInput);
    card.appendChild(qtyRow);

    // ── bits (D1) ───────────────────────────────────────────────────────────
    // Mandatory, and the buyer chooses the amount. The bounds come from the
    // node's mirror of the Worker's constants; the euro equivalent comes from
    // the Worker itself, and is the ONLY money this page renders — the fee, the
    // tax and the total are Stripe's page to draw (§7, D21).
    let bitsPerEuro: number | null = null;

    const bitsField = createBitsField({
        label: 'Bits on each code',
        hint: 'spending money for the account this code registers.',
    });
    card.appendChild(bitsField.row);

    // Organizations get a SECOND figure: an organization voucher carries two
    // locks (§4.13), one for the organization's own wallet and one for the
    // founding delegate's, and they are different amounts of money for
    // different purposes.
    const founderField = createBitsField({
        label: "Bits for the founder's own wallet",
        hint: "for the person who signs in for the organization; the organization's own "
            + 'wallet gets the figure above.',
    });
    card.appendChild(founderField.row);

    function syncType(): void {
        founderField.row.hidden = typeSelect.value !== 'organization';
    }
    typeSelect.addEventListener('change', syncType);
    syncType();

    const buyBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: isTestHookActive() ? 'Buy (test hook — skip Stripe)' : 'Continue to payment',
    });
    card.appendChild(buyBtn);
    card.appendChild(statusEl);

    const codesArea = createElement('div');
    card.appendChild(codesArea);

    /** The node's announced standalone site, once the runtime config resolves.
     *  Null in dev, where the card's address falls back to this origin + `dp`. */
    let siteBaseUrl: string | null = null;

    // ── Turn a code into an invitation (§14.3) ──────────────────────────────
    const invitationSection = renderInvitationSection();
    card.appendChild(invitationSection);

    const linkRow = createElement('div', { className: 'cid-auth-link-row' });
    const registerLink = createElement('a', {
        className: 'cid-auth-link', textContent: 'Register an identity',
    });
    registerLink.href = '#';
    registerLink.addEventListener('click', (e) => { e.preventDefault(); callbacks.onRegister(); });
    const backLink = createElement('a', { className: 'cid-auth-link', textContent: 'Back to sign in' });
    backLink.href = '#';
    backLink.addEventListener('click', (e) => { e.preventDefault(); callbacks.onExit(); });
    linkRow.appendChild(registerLink);
    linkRow.appendChild(backLink);
    card.appendChild(linkRow);

    wrapper.appendChild(card);
    container.appendChild(wrapper);

    // Runtime payment posture. A waived node sells nothing, so the whole page is
    // pointless there — say so rather than take someone to a checkout that would
    // charge for something the server hands out free. Fails CLOSED like every
    // other consumer of this config: an unreachable endpoint keeps payment on.
    buyBtn.setAttribute('disabled', '');
    void (async () => {
        const cfg = await fetchRegistrationConfig();
        siteBaseUrl = cfg.siteBaseUrl;
        bitsField.setBounds(cfg.bits);
        founderField.setBounds(cfg.bits);
        // The registration gate (docs/cid-governance.md M12): a node that
        // registers by invitation only sells no codes and turns none into an
        // invitation, and neither does one that could not be asked. Checked
        // before the waived branch — it is the stronger statement.
        const offer = registrationOffer(cfg);
        if (!offer.buyCodes) {
            intro.textContent = cfg.fromServer
                ? 'This Catalog.ID server registers new members by invitation only, so it does not sell '
                    + 'registration codes. If you were given a code or an invitation, use "Register an '
                    + 'identity" below.'
                : offer.notice ?? '';
            typeRow.remove();
            qtyRow.remove();
            bitsField.remove();
            founderField.remove();
            buyBtn.remove();
            invitationSection.remove();
            return;
        }
        if (!cfg.paymentRequired) {
            intro.textContent = 'Registration on this server is currently free, so there is nothing to buy. '
                + 'Anyone can register without a voucher.';
            typeRow.remove();
            qtyRow.remove();
            bitsField.remove();
            founderField.remove();
            buyBtn.remove();
            // Nothing is sold here, so there is no code to turn into anything.
            invitationSection.remove();
            return;
        }
        buyBtn.removeAttribute('disabled');
        // The pilot Worker's euro rate is gone with it (apps/bitcash/docs/plans/bitcash-v2.md M12; the
        // commercial track's sale configuration replaces it, M16): the label says
        // "100 000 bits" and nothing else, which is still true.
    })();

    buyBtn.addEventListener('click', () => {
        const quantity = Number(qtyInput.value);
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_VOUCHER_QUANTITY) {
            showError(`Choose a whole number of codes between 1 and ${MAX_VOUCHER_QUANTITY}.`);
            return;
        }
        const registrationType = typeSelect.value as 'personal' | 'organization';
        const chosenBits = bitsField.read();
        if (chosenBits.problem !== null) { showError(chosenBits.problem); return; }
        const bits = chosenBits.bits;

        const organization = registrationType === 'organization';
        let founderBits: number | undefined;
        if (organization) {
            const chosenFounder = founderField.read();
            if (chosenFounder.problem !== null) { showError(`Founder's bits: ${chosenFounder.problem}`); return; }
            founderBits = chosenFounder.bits;
        }

        // Open the popup synchronously, inside the click's user activation and
        // before any await, or the browser may block it (same rule as the
        // wizard's Stripe popup). It sits blank while the codes are minted.
        const popup = window.open('', 'cid-stripe-checkout', 'width=480,height=760');

        buyBtn.setAttribute('disabled', '');
        buyBtn.textContent = 'Waiting for payment…';
        errorEl.classList.remove('cid-pane-error--visible');
        statusEl.textContent = 'Preparing your codes…';

        void (async () => {
            // ── mint BEFORE the checkout opens (§4.3) ────────────────────────
            // A lock is keyed on a public key derived from the code, and the
            // WEBHOOK creates the locks — so the keys have to exist before the
            // card is charged. One scrypt per code, ~0.1 s each, hence the
            // progress line.
            let batch: MintedVoucherBatch;
            try {
                batch = await mintVoucherBatch({
                    registrationType, quantity, bits, founderBits,
                    onProgress: (done, total) => {
                        statusEl.textContent = total > 1
                            ? `Preparing your codes… (${done} of ${total})` : 'Preparing your code…';
                    },
                });
            } catch (e) {
                try { popup?.close(); } catch { /* already closed */ }
                showError(e instanceof Error ? e.message : 'Could not prepare the codes');
                return;
            }

            statusEl.textContent = 'Complete your payment in the Stripe window.';
            const result = await buyVouchers({
                popup,
                registrationType,
                quantity,
                bits,
                founderBits,
                vouchers: batch.specs,
                onStatus: (label) => { statusEl.textContent = label; },
            });

            if (result.status === 'complete') {
                showCodes(batch, result.expiresAt);
                return;
            }
            if (result.status === 'cancelled') {
                resetButton();
                // The codes are DISCARDED on a cancel, deliberately: nothing was
                // charged, no lock exists, and keeping them would leave codes
                // lying about that open nothing.
                statusEl.textContent = 'Payment cancelled. You can try again.';
                return;
            }
            resetButton();
            showError(result.error);
        })();
    });

    function resetButton(): void {
        buyBtn.removeAttribute('disabled');
        buyBtn.textContent = isTestHookActive() ? 'Buy (test hook — skip Stripe)' : 'Continue to payment';
    }

    function showError(message: string): void {
        statusEl.textContent = '';
        errorEl.textContent = message;
        errorEl.classList.add('cid-pane-error--visible');
        resetButton();
    }

    /**
     * The codes, shown ONCE — from this device's own stash, because the node
     * never saw them and could not hand them back (§4.3). A reload loses them
     * for good (D26), which is why Print and Download sit right here.
     */
    function showCodes(batch: MintedVoucherBatch, expiresAt: string): void {
        statusEl.textContent = '';
        typeRow.remove();
        qtyRow.remove();
        bitsField.remove();
        founderField.remove();
        buyBtn.remove();
        // Shared with the operator pane and the organization ledger — see
        // auth/voucher-code-display.ts for why there is only one of these.
        renderVoucherCodes(codesArea, batch.vouchers.map(v => v.code), { expiresAt });

        const site = {
            siteBaseUrl, origin: window.location.origin, dashboardPort: getDashboardPort(),
        };
        const siteUrl = activationUrlFor({}, site);
        const cards: VoucherCardData[] = batch.vouchers.map(v => ({
            code: v.code,
            registrationType: batch.registrationType,
            bits: batch.bits,
            url: voucherUrl(siteBaseUrl ?? window.location.origin, v.code),
            siteUrl,
        }));

        const actions = createElement('div', { className: 'cs-pane-toolbar' });
        const printBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cs-btn--small',
            textContent: cards.length === 1 ? 'Print voucher' : 'Print vouchers',
        });
        // Synchronous from the click: printPdf opens its window immediately
        // (popup rules).
        printBtn.addEventListener('click', () => { void printPdf(buildVoucherCardSheet(cards)); });
        actions.appendChild(printBtn);

        // PNG is offered for a SINGLE code only. Each download opens the
        // desktop's native Save dialog (memory: WebKitGTK downloads), so a
        // 25-code batch would be twenty-five dialogs; Print puts the whole
        // batch on one sheet instead, which is what a batch buyer wants.
        if (cards.length === 1) {
            const pngBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Download PNG',
            });
            pngBtn.addEventListener('click', async () => {
                pngBtn.setAttribute('disabled', '');
                try {
                    const { exportCardPng } = await import('../components/card-png');
                    downloadBlob(
                        await exportCardPng(buildVoucherCardDocument(cards[0])), 'voucher.png');
                } finally {
                    pngBtn.removeAttribute('disabled');
                }
            });
            actions.appendChild(pngBtn);
        }
        codesArea.appendChild(actions);

        codesArea.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: `Each code carries ${bitsEuroLabel(batch.bits, bitsPerEuro)}`
                + (batch.founderBits !== undefined
                    ? `, plus ${bitsEuroLabel(batch.founderBits, bitsPerEuro)} for the founder's own wallet,`
                    : '')
                + ' which reaches the account at the end of its registration. Anyone holding a code '
                + 'can spend it — to hand one over safely, personalize it below.',
        }));
    }

    /**
     * The second half of the storefront: paste a registration code you hold and
     * address it to one person. Everything about the mint happens on this device
     * — the code, the scrypt, the digests — and the code is shown ONCE.
     */
    function renderInvitationSection(): HTMLElement {
        const section = createElement('div', { className: 'cid-recovery-section' });
        section.appendChild(createElement('h3', {
            className: 'cid-wizard-heading', textContent: 'Personalize a voucher for someone else',
        }));
        section.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'A registration code can be spent by anyone who reads it. Personalizing it '
                + 'turns it into an invitation addressed to one person: it fixes an email address, a '
                + 'phone number or a domain, and only somebody who can verify what it fixes can '
                + 'register with it. The bits move onto the invitation too, and come back to you if '
                + 'it is never used. This is how you hand a code over — it is not a step for a code '
                + 'you mean to use yourself.',
        }));

        const invError = createElement('div', { className: 'cid-pane-error' });
        section.appendChild(invError);

        const form = createElement('div');
        section.appendChild(form);

        const codeField = createElement('div', { className: 'cid-auth-field' });
        codeField.appendChild(createElement('label', {
            className: 'cid-wizard-text', textContent: 'Your registration code',
        }));
        const codeInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
        codeInput.type = 'text';
        codeInput.placeholder = VOUCHER_CODE_PLACEHOLDER;
        codeInput.autocomplete = 'off';
        codeInput.spellcheck = false;
        // Prefilled when the entry page sent its holder here (§7): they typed
        // the code once already, and typing it a second time is where a
        // transcription error gets introduced.
        const prefill = options.personalizeCode ?? personalizeCodeFromFragment();
        if (prefill) codeInput.value = formatVoucherCode(prefill);
        codeField.appendChild(codeInput);
        form.appendChild(codeField);

        const kindField = createElement('div', { className: 'cid-auth-field' });
        kindField.appendChild(createElement('label', {
            className: 'cid-wizard-text', textContent: 'What is it for',
        }));
        const kindSelect = createElement('select', { className: 'cid-auth-input' }) as HTMLSelectElement;
        for (const [value, label] of [
            ['personal', 'A personal identity'],
            ['organization', 'An organization'],
        ] as const) {
            const opt = createElement('option', { textContent: label }) as HTMLOptionElement;
            opt.value = value;
            kindSelect.appendChild(opt);
        }
        kindField.appendChild(kindSelect);
        form.appendChild(kindField);

        const domainField = createElement('div', { className: 'cid-auth-field' });
        domainField.appendChild(createElement('label', {
            className: 'cid-wizard-text', textContent: 'Domain the organization will register',
        }));
        const domainInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
        domainInput.type = 'text';
        domainInput.placeholder = 'example.com';
        domainField.appendChild(domainInput);
        form.appendChild(domainField);

        const emailField = createElement('div', { className: 'cid-auth-field' });
        const emailLabel = createElement('label', { className: 'cid-wizard-text' });
        emailField.appendChild(emailLabel);
        const emailInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
        emailInput.type = 'email';
        emailInput.placeholder = 'someone@example.com';
        emailInput.autocomplete = 'off';
        emailField.appendChild(emailInput);
        form.appendChild(emailField);

        const phoneField = createElement('div', { className: 'cid-auth-field' });
        const phoneLabel = createElement('label', { className: 'cid-wizard-text' });
        phoneField.appendChild(phoneLabel);
        const phoneInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
        phoneInput.type = 'tel';
        phoneInput.placeholder = '+31 6 1234 5678';
        phoneInput.autocomplete = 'off';
        phoneField.appendChild(phoneInput);
        form.appendChild(phoneField);

        const makeBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit', textContent: 'Create invitation',
        });
        form.appendChild(makeBtn);

        // A flex column with the wizard's gap: a bare <div> would lay the
        // read-only code and link inputs out inline, at the browser's default
        // 20-character width and with nothing between them.
        const resultArea = createElement('div', { className: 'cid-wizard-step' });
        section.appendChild(resultArea);

        function syncKind(): void {
            const organization = kindSelect.value === 'organization';
            domainField.hidden = !organization;
            // For an organization the channels belong to the FOUNDING DELEGATE —
            // the person who will sign in for it. An organization account has no
            // mailbox of its own.
            emailLabel.textContent = organization
                ? "Founder's email (optional)" : 'Email address (optional if you give a phone)';
            phoneLabel.textContent = organization
                ? "Founder's phone (optional)" : 'Phone number (optional if you give an email)';
        }
        kindSelect.addEventListener('change', syncKind);
        syncKind();

        makeBtn.addEventListener('click', () => { void create(); });

        /**
         * §4.4, in the order the plan fixes: **CID first, Worker second.**
         *
         * A failure at the CID moves nothing at all. A failure at the Worker
         * leaves a valid invitation whose bits are still on the voucher lock —
         * recoverable by repeating the relock, which is idempotent. The reverse
         * order would strand bits under an invitation key that names no
         * invitation, and nothing could find them again.
         */
        async function create(): Promise<void> {
            invError.classList.remove('cid-pane-error--visible');
            makeBtn.setAttribute('disabled', '');
            makeBtn.textContent = 'Creating…';
            const registrationType = kindSelect.value as 'personal' | 'organization';
            try {
                const draft = await draftInvitation({
                    voucherCode: codeInput.value,
                    registrationType,
                    email: emailInput.value,
                    phone: phoneInput.value,
                    domain: domainInput.value,
                });

                // 1. CID.
                const resp = await createInvitation(draft.request);

                // 2. Worker. Never fatal: the invitation exists and works.
                makeBtn.textContent = 'Moving the bits…';
                // How many bits this voucher actually carries. It CANNOT come
                // from the form above — that field prices a PURCHASE, and the
                // code being personalized may have been bought anywhere, for
                // any amount, by anyone. The node does not know either (§1). So
                // ask the Worker, through the lock key this code derives; a
                // lock that cannot be read leaves the card without a bits line
                // rather than printing a number nobody checked.
                let lockedBits: number | undefined;
                try {
                    const info = await bitcashWorker().lockInfo(lockKeyFor(draft.voucherPublicKey, 0));
                    if (info.exists && !info.closed) lockedBits = unitsToWholeBit(info.balance) ?? undefined;
                } catch { /* no figure — the card prints without the bits line */ }
                const legs = relockPlanFor({
                    voucher: {
                        lockKey: lockKeyFor(draft.voucherPublicKey, 0),
                        founderLockKey: registrationType === 'organization'
                            ? lockKeyFor(draft.voucherPublicKey, 1)
                            : undefined,
                        publicKeyHex: draft.voucherPublicKeyHex,
                    },
                    invitation: {
                        publicKey: draft.invitationPublicKey,
                        publicKeyHex: draft.invitationPublicKeyHex,
                    },
                    registrationType,
                    expiresAt: resp.expiresAt,
                });
                const relock = await relockBits(legs, draft.voucherSeed, { worker: bitcashWorker() });

                form.remove();
                showInvitation(draft, resp.expiresAt, resp.invitationId, relock, legs, lockedBits);
            } catch (err) {
                makeBtn.removeAttribute('disabled');
                makeBtn.textContent = 'Create invitation';
                invError.textContent = err instanceof Error ? err.message : 'Could not create the invitation';
                invError.classList.add('cid-pane-error--visible');
            }
        }

        function showInvitation(
            draft: InvitationDraft, expiresAt: string, invitationId: number,
            relock: RelockOutcome, legs: RelockLeg[], lockedBits: number | undefined,
        ): void {
            resultArea.innerHTML = '';
            resultArea.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'This code is shown ONLY NOW — the node never received it and cannot show '
                    + 'it again. Print the card, download the PNG, or copy it somewhere safe. '
                    + 'Whoever holds it can collect its bits, so treat it as money until it is used.',
            }));

            const codeField = createElement('div', { className: 'cid-auth-field' });
            codeField.appendChild(createElement('label', {
                className: 'cid-wizard-text', textContent: 'Invitation code',
            }));
            const shown = createElement('input', {
                className: 'cid-auth-input cid-credential-value',
            }) as HTMLInputElement;
            shown.type = 'text';
            shown.value = draft.code;
            shown.readOnly = true;
            shown.addEventListener('click', () => shown.select());
            codeField.appendChild(shown);
            resultArea.appendChild(codeField);

            // The card's address: the node's announced standalone site when it has
            // one, else this origin with `dp` so a fresh tab finds the real server
            // (§11). Same helper the delegate invitation card uses.
            const site = {
                siteBaseUrl,
                origin: window.location.origin,
                dashboardPort: getDashboardPort(),
            };
            const url = activationUrlFor({ code: draft.code }, site);
            // The bits line and its own deadline (D20, D28): the card says what
            // the invitation carries and until when the money may be collected —
            // thirty days past the registration deadline, so a registration
            // begun on the last day still funds its print step.
            const carriesBits = relock.status === 'relocked' && lockedBits !== undefined;
            const cardData: InvitationCardData = {
                kind: draft.request.registrationType,
                code: draft.code,
                domain: draft.fixedDomain,
                email: draft.fixedEmail,
                phone: draft.fixedPhone,
                expiresAt: new Date(expiresAt),
                ...(carriesBits ? {
                    bits: lockedBits,
                    bitsExpireAt: new Date(Date.parse(expiresAt) + INVITATION_LOCK_GRACE_MS),
                } : {}),
                url,
                siteUrl: activationUrlFor({}, site),
            };

            const linkField = createElement('div', { className: 'cid-auth-field' });
            linkField.appendChild(createElement('label', {
                className: 'cid-wizard-text', textContent: 'Link for the invitee (the code is in it)',
            }));
            const linkInput = createElement('input', { className: 'cid-auth-input' }) as HTMLInputElement;
            linkInput.type = 'text';
            linkInput.value = url;
            linkInput.readOnly = true;
            linkInput.addEventListener('click', () => linkInput.select());
            linkField.appendChild(linkInput);
            resultArea.appendChild(linkField);

            const actions = createElement('div', { className: 'cs-pane-toolbar' });
            const printBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cs-btn--small', textContent: 'Print card',
            });
            // Synchronous from the click: printPdf opens its window immediately
            // (popup rules).
            printBtn.addEventListener('click', () => { void printPdf(buildInvitationCardSheet([cardData])); });
            actions.appendChild(printBtn);

            const pngBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Download PNG',
            });
            pngBtn.addEventListener('click', async () => {
                pngBtn.setAttribute('disabled', '');
                try {
                    const { exportInvitationCardPng } = await import('../components/booklet/invitation-card-png');
                    downloadBlob(await exportInvitationCardPng(cardData), `invitation-${invitationId}.png`);
                } finally {
                    pngBtn.removeAttribute('disabled');
                }
            });
            actions.appendChild(pngBtn);

            const copyBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Copy code',
            });
            copyBtn.addEventListener('click', async () => {
                await navigator.clipboard.writeText(draft.code);
                copyBtn.textContent = 'Copied!';
                setTimeout(() => { copyBtn.textContent = 'Copy code'; }, 2000);
            });
            actions.appendChild(copyBtn);
            resultArea.appendChild(actions);

            // The Worker leg, when it did not finish. The invitation is already
            // valid — this is about the money, and repeating it is safe (§4.4).
            if (relock.status === 'partial') {
                const retryRow = createElement('div', { className: 'cs-pane-toolbar' });
                const retryNote = createElement('p', {
                    className: 'cid-pane-note', textContent: relock.message,
                });
                resultArea.appendChild(retryNote);
                let pending = relock.pending;
                const retryBtn = createElement('button', {
                    className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Move the bits again',
                });
                retryBtn.addEventListener('click', () => {
                    retryBtn.setAttribute('disabled', '');
                    void (async () => {
                        const again = await relockBits(
                            pending.length > 0 ? pending : legs,
                            draft.voucherSeed, { worker: bitcashWorker() });
                        if (again.status === 'relocked') {
                            retryNote.textContent = 'The bits are on the invitation now.';
                            retryBtn.remove();
                            return;
                        }
                        pending = again.pending;
                        retryNote.textContent = again.message;
                        retryBtn.removeAttribute('disabled');
                    })();
                });
                retryRow.appendChild(retryBtn);
                resultArea.appendChild(retryRow);
            }

            resultArea.appendChild(createElement('p', {
                className: 'cid-pane-note',
                textContent: `Valid until ${new Date(expiresAt).toLocaleDateString()}.`
                    + (draft.fixedDomain ? ` Domain fixed to ${draft.fixedDomain}.` : '')
                    + (draft.fixedEmail ? ` Email fixed to ${draft.fixedEmail}.` : '')
                    + (draft.fixedPhone ? ` Phone fixed to ${draft.fixedPhone}.` : '')
                    + ' If it is never used, the registration code goes back into your hands when the '
                    + 'invitation expires — and so do its bits: keep the registration code, it is '
                    + 'what collects them back.',
            }));
        }

        return section;
    }
}

// ============================================================================
// Turn a code into an invitation (registration-restructure §14.3) — DOM-free core
// ============================================================================
//
// A registration code is bearer money: whoever reads it can spend it. An
// INVITATION is the same payment addressed to one person — it fixes an email, a
// phone, or (for an organization) a domain, and only somebody who can verify
// what it fixes may complete the registration. That is what makes "buy one for a
// friend" safe to put in a letter.
//
// The node never sees the `INV-` code: it is minted here, one scrypt turns it
// into a lookup id and a fixed-value salt, and only the lookup id and the SALTED
// DIGESTS of the fixed values are sent (§14.2). The domain is the exception and
// travels in the clear — it is public, and the node has to compare it at
// register/begin, where the domain claim is still encrypted.

// The bits helpers and the relock plan are not view concerns — both wizards,
// the delegate activation wizard and the organization's panes run them with no
// DOM in sight, and a wizard importing this module would drag pdfmake and
// pdf.js into its chunk. They live in `voucher-mint.ts` and `bits-release.ts`;
// re-exported here so the storefront's own test has one import site.
export {
    validateBits, bitsEuroLabel, mintVoucherBatch, defaultVoucherMintDeps,
    type BitsBounds, type MintedVoucher, type MintedVoucherBatch, type VoucherMintDeps,
} from './voucher-mint';
export {
    relockPlanFor, invitationLockConditions, INVITATION_LOCK_GRACE_MS, type RelockLeg,
} from './bits-release';

export interface InvitationFormInput {
    /** The `RGV-` code being converted. */
    voucherCode: string;
    registrationType: 'personal' | 'organization';
    /** Fixed email, as typed. Blank ⇒ not fixed. */
    email?: string;
    /** Fixed phone, as typed. Blank ⇒ not fixed. */
    phone?: string;
    /** Organization only: the domain the founder must claim. */
    domain?: string;
}

/**
 * What is wrong with the form, or null when it is ready to send. Pure, and the
 * one place the storefront's rules live.
 *
 * The rule that matters: an invitation MUST restrict something. An unrestricted
 * one would be the registration code the buyer already holds, with a shorter
 * life and no added safety — the node refuses it too, and this says so before a
 * round trip.
 */
export function validateInvitationForm(input: InvitationFormInput): string | null {
    if (!normalizeVoucherCode(input.voucherCode)) {
        return 'That does not look like a registration code. Codes start with RGV-.';
    }
    const email = (input.email ?? '').trim();
    const phone = (input.phone ?? '').trim();
    const domain = (input.domain ?? '').trim();

    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normaliseEmail(email))) {
        return 'That does not look like an email address.';
    }
    if (phone && normalisePhone(phone).replace(/\D/g, '').length < 6) {
        return 'That does not look like a phone number. Include the country code, for example +31 6 1234 5678.';
    }

    if (input.registrationType === 'organization') {
        if (!domain) return 'Enter the domain the organization will register.';
        if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(domain.toLowerCase().replace(/\.$/, ''))) {
            return 'That does not look like a domain name.';
        }
        return null;
    }

    if (!email && !phone) {
        return 'An invitation has to be addressed to somebody: give an email address, a phone '
            + 'number, or both. Without one it would just be the registration code you already have.';
    }
    return null;
}

/** Everything one minted invitation needs — the request to send, the plaintext
 *  to PRINT on the card (the node holds only the digests), and the key material
 *  the RELOCK needs (§4.4): the buyer's seed signs, the invitee's public half is
 *  the new condition. */
export interface InvitationDraft {
    /** Display form, `INV-XXXX-XXXX-XXXX-XXXX`. Shown once and never again. */
    code: string;
    request: CreateInvitationRequest;
    fixedEmail?: string;
    fixedPhone?: string;
    fixedDomain?: string;
    /** The `RGV-` code, normalised. Stays on this device: it signs the relock,
     *  and it is the RECLAIM path if the invitation is never used (§4.4). */
    voucherCode: string;
    /** The voucher's Ed25519 seed — the only thing that can move its bits. */
    voucherSeed: Uint8Array;
    /** Its public half, raw and hex: `lockKeyFor` wants the bytes, a condition
     *  wants the hex. Both come from the one derivation, so neither side has to
     *  decode the other. */
    voucherPublicKey: Uint8Array;
    voucherPublicKeyHex: string;
    /** The invitation's public half, raw and hex: the new lock's condition and
     *  the input to `lockKeyFor`. */
    invitationPublicKey: Uint8Array;
    invitationPublicKeyHex: string;
}

/** The non-deterministic calls, injectable so the draft is testable without
 *  two 32 MiB scrypts. */
export interface InvitationDraftDeps {
    mintCode: typeof mintInvitationCode;
    derive: typeof deriveInvitationSecrets;
    deriveVoucher: typeof deriveVoucherSecrets;
}

export const defaultInvitationDraftDeps: InvitationDraftDeps = {
    mintCode: mintInvitationCode,
    derive: deriveInvitationSecrets,
    deriveVoucher: deriveVoucherSecrets,
};

/**
 * Mint a code and build the create request from it. Throws the validation
 * message when the form is not ready — callers show it verbatim.
 *
 * The fixed values are NORMALISED before hashing, by the same functions the
 * validator will use on what the invitee types (NFC → trim → lowercase for
 * email; E.164 for phone). Getting that wrong would produce an invitation
 * nobody could ever redeem, and the failure would only show up at the
 * validator, after the card was printed.
 */
export async function draftInvitation(
    input: InvitationFormInput, deps: InvitationDraftDeps = defaultInvitationDraftDeps,
): Promise<InvitationDraft> {
    const problem = validateInvitationForm(input);
    if (problem) throw new Error(problem);

    const code = deps.mintCode();
    const secrets = await deps.derive(code);
    // The voucher's own derivation: its lookup id is what the node is given, and
    // its seed is what signs the relock a moment later. Both come out of ONE
    // scrypt, so it is run here rather than twice in the caller.
    const voucherCode = normalizeVoucherCode(input.voucherCode)!;
    const voucherSecrets = await deps.deriveVoucher(voucherCode);

    const email = (input.email ?? '').trim();
    const phone = (input.phone ?? '').trim();
    const domain = (input.domain ?? '').trim();
    const fixedEmail = email ? normaliseEmail(email) : undefined;
    const fixedPhone = phone ? normalisePhone(phone) : undefined;
    const fixedDomain = input.registrationType === 'organization' && domain
        ? domain.trim().toLowerCase().replace(/\.$/, '')
        : undefined;

    return {
        code: formatInvitationCode(code),
        fixedEmail,
        fixedPhone,
        fixedDomain,
        voucherCode,
        voucherSeed: voucherSecrets.seed,
        voucherPublicKey: voucherSecrets.publicKey,
        voucherPublicKeyHex: voucherSecrets.publicKeyHex,
        invitationPublicKey: secrets.publicKey,
        invitationPublicKeyHex: secrets.publicKeyHex,
        request: {
            registrationType: input.registrationType,
            voucherLookupId: voucherSecrets.lookupIdHex,
            lookupId: secrets.lookupIdHex,
            ...(fixedEmail ? { emailHash: hashFixedValue(secrets.saltHex, fixedEmail) } : {}),
            ...(fixedPhone ? { phoneHash: hashFixedValue(secrets.saltHex, fixedPhone) } : {}),
            ...(fixedDomain ? { domain: fixedDomain } : {}),
        },
    };
}
